import React, { useEffect, useState } from 'react'
import {
  Button,
  Card,
  Col,
  Container,
  Row,
  Spinner,
} from 'reactstrap'

import NotificationBox from './components/NotificationBox'

import useGetAlertUiData from './hooks/useGetAlertUiData'
import useGetAlertSettings from './hooks/useGetAlertSettings'
import useUpdateAlertSettings from './hooks/useUpdateAlertSettings'


const Notifications = () => {

  const {
    isPending: isUiLoading,
    isError: isUiError,
    alertUiData,
  } = useGetAlertUiData()

  const {
    isFailed,
    isGetting,
    alertSettings,
  } = useGetAlertSettings()

  const {
    isUpdating,
    updateAlertSettingsAPI,
  } = useUpdateAlertSettings()

  /*
   * This is the final state used by the UI.
   */
  const [categories, setCategories] = useState([])


  /*
   * Loading
   */
  const isLoading =
    isUiLoading || isGetting


  /*
   * Merge UI configuration with
   * saved organization settings.
   */
  useEffect(() => {

    if (!alertUiData) {
      return
    }

    const savedCategories = alertSettings?.alert_categories || []
    const savedTypes = alertSettings?.alert_types || []

    const mergedCategories =
      alertUiData.map((category) => {

        // match on categoryAlertId (Alert PK), not category.id (string key)
        const savedCategory =
          savedCategories.find(
            (item) => item.alert_cat === category.categoryAlertId
          )

        // options are label strings; use optionIds to resolve each label's Alert PK
        const selectedOptions =
          category.options.filter((label) => {
            const optionId = category.optionIds[label]
            return savedTypes.some(
              (saved) =>
                saved.alert_type_id === optionId &&
                saved.is_enabled
            )
          })

        return {
          ...category,
          enabled: savedCategory?.is_enabled ?? false,
          email: savedCategory?.email ?? '',
          selectedOptions,   // array of label strings, e.g. ["Completed", "Cancelled"]
        }
      })

    setCategories(mergedCategories)

  }, [alertUiData, alertSettings])


  /*
   * Child component changed.
   */
  const handleCategoryChange = (data) => {

    setCategories(
      (previous) =>

        previous.map((category) => {

          if (
            category.id !== data.id
          ) {
            return category
          }

          return {
            ...category,
            enabled: data.enabled,
            selectedOptions: data.selectedOptions,
            email: data.email,
          }
        })
    )
  }


  /*
   * Save settings.
   */
  const handleSave = () => {

    const alertCategories =
      categories.map((category) => ({
        alert_cat: category.categoryAlertId,
        is_enabled: category.enabled,
        email: category.email,
      }))

    const alertTypes = []

    categories.forEach((category) => {
      category.options.forEach((label) => {
        const optionId = category.optionIds[label]
        alertTypes.push({
          alert_type_id: optionId,
          is_enabled: category.selectedOptions.includes(label),
        })
      })
    })

    const payload = {
      alert_categories: alertCategories,
      alert_types: alertTypes,
    }

    updateAlertSettingsAPI(payload)
  }

  const [isNotificationEnabled,setIsNotificationEnabled] = useState(false)

  // const userData = JSON.parse(localStorage.getItem('userData'))
  // const roleName = userData?.user?.role

  // const editableRoles = [
  //   '00000000-0000-0000-0000-000000000001',
  //   '00000000-0000-0000-0000-000000000002',
  // ]

  // const isNotificationEnabled = !editableRoles.includes(roleName)

  useEffect(() => {
    const storedUserData = localStorage.getItem('userData')

    if (!storedUserData) {
      setIsNotificationEnabled(false)
      return
    }

    const userData = JSON.parse(storedUserData)
    const roleName = userData?.user?.role

    const editableRoles = [
      '00000000-0000-0000-0000-000000000001',
      '00000000-0000-0000-0000-000000000002',
    ]

    setIsNotificationEnabled(
      editableRoles.includes(roleName)
    )
  }, [])

  if (isLoading) {

    return (
      <Container fluid>

        <Card className="p-4 text-center">

          <Spinner />

        </Card>

      </Container>
    )
  }


  if (isUiError || isFailed) {

    return (
      <Container fluid>

        <Card className="p-4">

          <div className="text-danger">
            Failed to load notification settings.
          </div>

        </Card>

      </Container>
    )
  }


  return (
    <Container fluid>

      <Card className="p-4">

        <div className="pb-2 mb-0">

          <h5 className="fw-bold">
            Email Notifications
          </h5>

          <p
            className="text-muted mb-0"
            style={{
              fontSize: '0.7rem',
            }}
          >
            Choose which events send an email,
            and who receives it
          </p>

        </div>


        <Row>

          {categories.map((category) => (

            <NotificationBox
              key={category.id}
              {...category}
              disabled={!isNotificationEnabled}
              onChange={handleCategoryChange}
            />

          ))}

        </Row>


        <Row>

          <Col
            xs={6}
            className="ms-auto d-flex justify-content-end"
          >

            <Button
              onClick={handleSave}
              disabled={isUpdating || !isNotificationEnabled}
              style={{
                backgroundColor: '#24ce57',
                width: '100%',
                color: 'white',
                fontWeight: 'bold',
              }}
            >
              {isUpdating ? 'Saving...' : 'Save Alert Settings'}
            </Button>

          </Col>

        </Row>

      </Card>

    </Container>
  )
}

export default Notifications